import { useCallback, useEffect, useState } from "react";

import _get from "lodash/get";
import { SearchResult } from "../../../../../api/types/searchResult";
import { ProductRewardSearchResult } from "@brandclub/common-ui";

import {
  getBestRewardProducts,
  getUnauthedBestRewardProducts,
} from "../../../../../api/graphQl/authenticated/BestRewards";
import { uniqBy } from "lodash";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { useApolloClient } from "@apollo/client";

type RESKEY = "BestRewards" | "UnauthedBestRewards";
type PurchaseRewardProductsResponse = {
  [key in RESKEY]: SearchResult<ProductRewardSearchResult>;
};

export const useFetchPurchaseRewardProducts = ({
  brandIds,
  pageSize = 25,
  autoInitFetch = true,
}: {
  brandIds?: number[];
  pageSize?: number;
  autoInitFetch?: boolean;
}) => {
  const apolloClient = useApolloClient();
  const [data, setData] = useState<ProductRewardSearchResult[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signedIn } = useSignedIn();
  const resKey: RESKEY = signedIn ? "BestRewards" : "UnauthedBestRewards";

  const fetchData = useCallback(
    async (pageNumber = 0) => {
      try {
        setError(null);

        const res =
          await apolloClient.query<PurchaseRewardProductsResponse>({
            query: signedIn
              ? getBestRewardProducts
              : getUnauthedBestRewardProducts,
            variables: {
              brandIds,
              page: {
                start: pageNumber,
                size: pageSize,
              },
            },
            fetchPolicy: "network-only",
          });
        const newData = _get(res, ["data", resKey, "items"], []);

        setData((d) => uniqBy([...d, ...newData], "stacklineSku"));
        const newHasMore = _get(res, ["data", resKey, "hasMore"], false);
        setHasMore(newHasMore);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("An error occurred while fetching purchase reward products");
        }
      }
    },
    [brandIds, pageSize, signedIn, resKey, apolloClient]
  );

  const fetchInit = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(0);
      setPage((p) => p + 1);
    } catch (e) {
      setPage(0);
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("An error occurred while fetching purchase reward products");
      }
    } finally {
      setPage(0);
      setLoading(false);
    }
  }, [fetchData]);

  const fetchMore = useCallback(async () => {
    try {
      setLoadingMore(true);
      await fetchData((page + 1) * pageSize);
    } finally {
      setPage((p) => p + 1);
      setLoadingMore(false);
    }
  }, [fetchData, page, pageSize]);

  useEffect(() => {
    if (autoInitFetch) {
      fetchInit();
    }
  }, [autoInitFetch, fetchInit]);

  return {
    data,
    loading,
    error,
    hasMore,
    fetchMore,
    loadingMore,
    fetchInit,
    page,
  };
};
